<script lang="ts">
	/**
	 * Delivery health for `/Settings/Admin/Applications` (Design §8.1, §13).
	 *
	 * The table exists because outbound delivery fails **quietly**. Nothing in the storefront
	 * changes when a receiver stops answering: purchases complete, entitlements are correct in the
	 * Store, and one customer simply cannot use what they bought, in someone else's product. The
	 * only evidence is here.
	 *
	 * Two things it deliberately says out loud:
	 *
	 * - **`dead` is not the end.** A dead letter enqueues a repair, and the repair is the row
	 *   flagged *Resync*. A table that showed only the dead row would read as "this customer is
	 *   broken" when the truthful reading is "this customer is being repaired, slowly, forever".
	 * - **Replay resends current state**, never history. Historical events carry versions the
	 *   receiver has already passed and a correct receiver discards them, so a replay-history
	 *   button would do nothing at all — which is worse than not offering one.
	 */

	import { onMount } from 'svelte';
	import {
		Badge,
		Button,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell
	} from 'flowbite-svelte';
	import {
		ConfirmButton,
		EmptyState,
		InfiniteLoadElement,
		Panel,
		RelativeDate,
		SelectField,
		WebhookResponseView
	} from '../../../components';
	import {
		listWebhookDeliveries,
		replayApplication,
		retryWebhookDelivery
	} from '../../../AppNetwork';
	import type { StoreApplication, WebhookDelivery } from '../../../AppTypes';
	import { toastError, toastSuccess } from '../../../stores/toast';

	export let applications: StoreApplication[] = [];

	const PAGE_SIZE = 25;

	let deliveries: WebhookDelivery[] = [];
	let total: number = 0;
	let loading: boolean = true;
	let loadError: string = '';
	let busyUUID: string = '';
	let replayingUUID: string = '';

	let sentinel: InfiniteLoadElement | undefined;
	let applicationFilter: string = '';
	let statusFilter: string = '';

	const statusOptions = [
		{ value: '', name: 'Any status' },
		{ value: 'pending', name: 'Queued' },
		{ value: 'delivering', name: 'In flight' },
		{ value: 'delivered', name: 'Delivered' },
		{ value: 'failed', name: 'Retrying' },
		{ value: 'dead', name: 'Dead letters' }
	];

	const statusColors: Record<string, 'green' | 'blue' | 'yellow' | 'red' | 'dark'> = {
		pending: 'dark',
		delivering: 'blue',
		delivered: 'green',
		failed: 'yellow',
		dead: 'red'
	};

	$: applicationOptions = [
		{ value: '', name: 'Every application' },
		...applications.map(application => ({
			value: application.abstractIDAppUUID,
			name: application.name
		}))
	];

	async function fnLoad(reset: boolean = true): Promise<void> {
		if (reset) {
			loading = true;
			deliveries = [];
		}
		loadError = '';
		try {
			const page = await listWebhookDeliveries({
				applicationID: applicationFilter,
				status: statusFilter,
				limit: PAGE_SIZE,
				offset: reset ? 0 : deliveries.length
			});
			deliveries = reset ? page.items : [...deliveries, ...page.items];
			total = page.total;
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	async function fnRetry(delivery: WebhookDelivery): Promise<void> {
		busyUUID = delivery.uuid;
		try {
			await retryWebhookDelivery(delivery.uuid);
			toastSuccess('Queued for the next poll.');
			await fnLoad();
		} catch (error) {
			toastError('The delivery was not queued.', (error as Error).message);
		} finally {
			busyUUID = '';
		}
	}

	async function fnReplay(application: StoreApplication): Promise<void> {
		replayingUUID = application.abstractIDAppUUID;
		try {
			const result = await replayApplication(application.abstractIDAppUUID);
			toastSuccess(
				`${result.enqueued} ${result.enqueued === 1 ? 'entitlement' : 'entitlements'} queued for ${application.name}.`
			);
			await fnLoad();
		} catch (error) {
			toastError('Nothing was replayed.', (error as Error).message);
		} finally {
			replayingUUID = '';
		}
	}

	async function fnLoadMore(): Promise<void> {
		await fnLoad(false);
		sentinel?.resetVisibilityTracking();
		sentinel?.updateState();
	}

	async function fnFilterChanged(): Promise<void> {
		await fnLoad();
	}

	/** The application the filter names, and only when it can actually receive anything. */
	$: selectedApplication = applications.find(
		application =>
			application.abstractIDAppUUID === applicationFilter && application.webhookURL !== null
	);
	$: hasMore = deliveries.length < total;

	onMount(() => fnLoad());
</script>

<Panel title="Delivery health" faIcon="paper-plane">
	<svelte:fragment slot="titleRight">
		<span class="text-default text-xs">{total} {total === 1 ? 'delivery' : 'deliveries'}</span>
	</svelte:fragment>

	<div class="space-y-4">
		<p class="text-default text-sm">
			Outbound delivery fails quietly: the store stays correct and one customer simply cannot use
			what they bought. A <strong>dead letter</strong> is not the end of it: the store enqueues a
			repair alongside it, shown here as a <strong>Resync</strong>, which retries slowly and
			indefinitely until the receiver comes back.
		</p>

		<div class="controlpanel flex flex-wrap items-center gap-3">
			<SelectField
				items={applicationOptions}
				bind:value={applicationFilter}
				on:change={fnFilterChanged}
				placeholder="Application"
				size="sm"
				class="w-56"
			/>
			<SelectField
				items={statusOptions}
				bind:value={statusFilter}
				on:change={fnFilterChanged}
				placeholder="Status"
				size="sm"
				class="w-44"
			/>
			<Button
				size="sm"
				color={statusFilter === 'dead' ? 'primary' : 'alternative'}
				on:click={() => {
					statusFilter = statusFilter === 'dead' ? '' : 'dead';
					fnFilterChanged();
				}}
			>
				<i class="fa-solid fa-skull-crossbones mr-2" />Dead letters
			</Button>
			<Button size="sm" color="alternative" on:click={() => fnLoad()}>
				<i class="fa-solid fa-arrows-rotate mr-2" />Refresh
			</Button>
		</div>

		{#if selectedApplication !== undefined}
			<div class="flex flex-wrap items-center gap-2">
				<span class="card-label">Repair</span>
				<!-- Scoped to the application the filter already names, rather than a row of buttons:
				     a replay is a bulk resend and the operator should have picked its target on
				     purpose. -->
				<ConfirmButton
					size="xs"
					color="alternative"
					disabled={replayingUUID !== ''}
					confirmText={`Resend every entitlement this application is scoped to, at its current version? This is a repair, not a history replay, so nothing older than the receiver already holds is sent.`}
					on:confirm={() => fnReplay(selectedApplication)}
				>
					Resend current state
				</ConfirmButton>
			</div>
		{/if}

		{#if loading}
			<EmptyState variant="loading" title="Loading deliveries…" />
		{:else if loadError.length > 0}
			<EmptyState variant="error" title="Deliveries could not be loaded." description={loadError}>
				<Button color="alternative" size="sm" on:click={() => fnLoad()}>Try again</Button>
			</EmptyState>
		{:else if deliveries.length === 0}
			<EmptyState
				title="No delivery matches."
				description="Nothing has been queued for these filters. An application with no webhook URL is pull-only and never appears here."
			/>
		{:else}
			<Table divClass="relative overflow-x-auto">
				<TableHead>
					<TableHeadCell>Application</TableHeadCell>
					<TableHeadCell>Event</TableHeadCell>
					<TableHeadCell>Status</TableHeadCell>
					<TableHeadCell>Attempts</TableHeadCell>
					<TableHeadCell>Next attempt</TableHeadCell>
					<TableHeadCell>Last response</TableHeadCell>
					<TableHeadCell />
				</TableHead>
				<TableBody>
					{#each deliveries as delivery (delivery.uuid)}
						<TableBodyRow>
							<TableBodyCell class="dataTable">
								<div class="font-medium">{delivery.applicationName}</div>
								{#if delivery.userUUID}
									<div class="text-default text-xs font-mono break-all">{delivery.userUUID}</div>
								{/if}
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								<div>{delivery.eventType}</div>
								<div class="text-default text-xs">
									{delivery.entitlementKey ?? '—'}
									{#if delivery.version !== null}
										· v{delivery.version}
									{/if}
								</div>
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								<Badge color={statusColors[delivery.status] ?? 'dark'}>{delivery.status}</Badge>
								{#if delivery.isResync}
									<!-- The repair a dead letter enqueued. It never dead-letters itself. -->
									<Badge color="purple" class="ml-1">resync</Badge>
								{/if}
							</TableBodyCell>
							<TableBodyCell class="dataTable">{delivery.attemptCount}</TableBodyCell>
							<TableBodyCell class="dataTable">
								{#if delivery.deliveredAt}
									<RelativeDate date={delivery.deliveredAt} />
								{:else if delivery.nextAttemptAt}
									<RelativeDate date={delivery.nextAttemptAt} />
								{:else}
									<span class="text-default">—</span>
								{/if}
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								<div>{delivery.lastResponseStatus ?? '—'}</div>
								{#if delivery.lastError}
									<div class="text-default text-xs break-all">{delivery.lastError}</div>
								{/if}
								<!--
									What the receiver actually said. The status and the error between them
									cannot answer "why" — and on a success they do not even raise the
									question, which is the case this exists for.
								-->
								<WebhookResponseView {delivery} />
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								{#if delivery.status !== 'delivered'}
									<Button
										size="xs"
										color="alternative"
										disabled={busyUUID === delivery.uuid}
										on:click={() => fnRetry(delivery)}
									>
										Retry now
									</Button>
								{/if}
							</TableBodyCell>
						</TableBodyRow>
					{/each}
				</TableBody>
			</Table>

			{#if hasMore}
				<!-- The sentinel fires on the false→true edge only, so it is reset after each page:
				     a short page in a tall viewport would otherwise stall with it still on screen. -->
				<InfiniteLoadElement bind:this={sentinel} on:becomeVisible={fnLoadMore} />
			{/if}
		{/if}
	</div>
</Panel>
